<template>
  <div class="hero-section-wrap" :class="$config.project">
    <div class="hero-section" :class="[{ hide: scrolledDown }, $config.project]">
      <div class="container">
        <div class="row">
          <div class="col col-8">
            <Breadcrumb
              class="breadcrumb" :breadcrumb="[
                { name: 'Courses', slug: '/courses/' },
                { name: course.name, slug: '' }
              ]"
            />
            <h1 class="h3">
              <span class="course-name">
                {{ course.name }}
              </span>
              <div v-if="labels.length" class="labels">
                <span
                  v-for="l in labels"
                  :key="l.key"
                  class="label"
                  :class="l.key"
                >
                  <Component :is="require(`../../assets/svg/courses/icon-label-${l.key}.svg?vue-component`)" />
                  {{ l.name }}
                </span>
              </div>
            </h1>
            <div class="instructor body-1">
              <span v-for="(i, k) in course.instructorsArr" :key="k">
                {{ `${k === 0 ? 'with' : 'and'}` }}
                <a v-if="$config.platform === 'ds'" :href="`${$config.siteUrl}/instructors/${i.slug}`">
                  {{ `${i.name}` }}
                </a>
                <span v-else>{{ `${i.name}` }}</span>
              </span>
              <Rating v-if="course.reviews" :reviews="course.reviews" />
            </div>
            <template v-if="isTest">
              <span class="updated">
                <RefreshIcon />Updated 03/2025
              </span>
            </template>
            <p v-if="course.shortDescription" class="description">
              {{ course.shortDescription }}
            </p>
            <div v-if="!course.progress && !course.certificate && !course.started" class="info">
              <span>
                <PlayIcon />{{ course.totalHours }} {{ course.totalHours === 1 ? 'hour' : 'hours' }} of content
              </span>
              <span class="dot" />
              <span>
                <PeopleIcon />{{ course.enrollments }} students
              </span>
            </div>
            <div v-else class="info">
              <Progress :progress="course.progress" :data="course.lessons" :certificate="course.certificate" />
            </div>
          </div>
          <div class="col col-4">
            <HeroCard class="desktop" :course="course" />
          </div>
        </div>
      </div>
    </div>
    <div
      class="hero-section sticky"
      :class="[{ show: scrolledDown, 'hide-sticky': scrolledUp, 'sidebar-expanded': $store.state.sideBarExpanded }, $config.project]"
    >
      <div class="container">
        <div class="row">
          <div class="col col-8">
            <h1 class="h3">
              {{ course.name }}
              <div v-if="labels.length" class="labels">
                <span
                  v-for="l in labels"
                  :key="l.key"
                  class="label"
                  :class="l.key"
                >
                  <Component :is="require(`../../assets/svg/courses/icon-label-${l.key}.svg?vue-component`)" />
                  {{ l.name }}
                </span>
              </div>
            </h1>
            <div class="instructor body-1">
              <span v-for="(i, k) in course.instructorsArr" :key="k">
                {{ `${k === 0 ? 'A course by' : 'and'}` }}
                <a v-if="$config.platform === 'ds'" :href="`${$config.siteUrl}/instructors/${i.slug}`" target="_blank">
                  {{ `${i.name}` }}
                </a>
                <span v-else>{{ `${i.name}` }}</span>
              </span>
            </div>
          </div>
          <div class="col col-4">
            <HeroCard class="desktop" sticky :course="course" />
          </div>
        </div>
      </div>
    </div>
    <div class="hero-section sticky sticky-mobile" :class="{ show: scrolledDown, 'hide-sticky': scrolledUp, 'banner': course.promoEndDate && course.status !== 'locked', 'lms': $config.project === 'lms' }">
      <div class="container">
        <div v-if="isLms">
          <img :src="`${$config.imagesUrl}/courses/${course.smallImage}`">
          <div
            v-if="course.simpleLmsFree && (($store.getters['auth/user'].simpleLms && !course.purchased) || $config.project === 'site')" :class="{ disabled: previewMode }" class="btn btn-primary"
            @click="$store.dispatch('courses/enrollNow', { courseId: course.id, courseSlug: course.slug })"
          >
            Enroll Now
          </div>
          <nuxt-link
            v-else
            :to="`/courses/${course.slug}/`" class="btn btn-primary"
          >
            {{ course.progress ? 'Continue learning' : 'Start learning' }}
          </nuxt-link>
        </div>
        <div v-else-if="!isLms && !course.simpleLmsFree">
          <div v-if="course.promoEndDate && course.status !== 'locked'" class="limited-time-only-banner">
            Limited-Time Offer
          </div>
          <div class="price-wrapper">
            <p v-if="course.promo" class="old-price h4">
              {{ course.currency.sign }}{{ course.originalPrice }}
            </p>
            <p class="h1 new-price">
              {{ course.currency.sign }}{{ course.price }}
            </p>
            <p v-if="!course.promo" class="caption">
              Lifetime access
            </p>
          </div>
          <a
            :href="`${$config.siteUrl}/checkout/?plan=${course.productHandle}&ptype=individual-course${course.promoCode ? `&coupon=${course.promoCode}` : ''}`"
            class="btn"
            :class="[!course.promo ? 'btn-primary btn-lg' : 'btn-primary-yellow btn-lg']"
          >
            {{ !course.promo ? 'Buy now' : `Get ${percentOff}% OFF` }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Breadcrumb from './Breadcrumb';
import HeroCard from './HeroCard';
import Rating from '../Rating';
import PlayIcon from '../../assets/svg/icon-play-circle.svg?vue-component';
import PeopleIcon from '../../assets/svg/icon-people.svg?vue-component';
import RefreshIcon from '../../assets/svg/icon-refresh.svg?vue-component';
import Progress from '../Progress';

export default {
  components: {
    Breadcrumb, Rating, PlayIcon, PeopleIcon, RefreshIcon, HeroCard, Progress,
  },
  props: {
    course: { type: Object, required: true },
  },
  data() {
    return {
      scrolledDown: false,
      scrolledUp: false,
    };
  },
  computed: {
    isTest() {
      const isDSTest = this.$config.platform === 'ds' && [1009381, 1009382, 1009383, 728610].includes(this.$store.getters['auth/user'].id) && this.course.id === 51;
      const isFATest = this.$config.platform === 'fa' && [1006886, 1006890, 1007543, 728610].includes(this.$store.getters['auth/user'].id) && this.course.id === 97;

      if (this.$config.project === 'lms' && (isDSTest || isFATest)) {
        return true;
      }

      return false;
    },
    label() {
      // If the course is purchased or the user has an active subscription, don't show the label for free courses
      if (this.course.label === 'free' && (this.course.purchased || this.$store.getters['auth/user'].activeSubscription)) {
        return false;
      }

      if (this.course.label === 'trending') {
        return 'trending topic';
      }

      return this.course.label;
    },
    labels() {
      if (!this.course.labels) {
        return [];
      }
      // If the course is purchased or the user has an active subscription, don't show the label for free courses
      if (this.course.labels.includes('free') && (this.course.purchased || this.$store.getters['auth/user'].activeSubscription)) {
        return this.course.labels.filter((l) => l !== 'free').map((l) => ({ name: l, key: l }));
      }

      if (this.course.labels.includes('trending')) {
        return this.course.labels.map((l) => (l.includes('trending') ? { name: 'trending topic', key: l } : { name: l, key: l }));
      }

      return this.course.labels.map((l) => ({ name: l, key: l }));
    },
    percentOff() {
      return Math.round((1 - (this.course.price / this.course.originalPrice)) * 100);
    },
    isLms() {
      return this.course.purchased || this.$store.getters['auth/user'].activeSubscription || (this.$config.project === 'lms' && !this.$store.getters['auth/user'].activeSubscription && !this.$store.getters['auth/user'].simpleLms) || (this.$config.project === 'lms' && (this.course.simpleLmsFree));
    },
  },
  mounted() {
    this.handleScroll();
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      let heroCard = null;
      let heroCardHeight = 0;
      if (this.$store.state.layout === 'mobile') {
        heroCard = document.querySelector('.hero-card.mobile');

        if (this.$config.project !== 'lms') {
          heroCardHeight = heroCard.offsetHeight;
        } else {
          heroCardHeight = heroCard.offsetHeight + 220;
        }
      } else {
        heroCard = document.querySelector('.hero-section:not(.sticky) .hero-card');
        heroCardHeight = heroCard.offsetHeight + 120;
      }

      const headerHeight = document.querySelector('.header').offsetHeight;
      if (window.scrollY >= ((heroCardHeight) - (headerHeight)) && !this.scrolledDown) {
        this.scrolledDown = true;
        this.scrolledUp = false;
      } else if (window.scrollY < 10 && this.scrolledDown) {
        this.scrolledUp = true;
        this.scrolledDown = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
main:has(> .banner-holder) {
  .hero-section.sticky:not(.lms) {
    top: 176px;
  }
}

.hero-section {
  border-radius: 0 0 60px 0;
  background: var(--gradients-hero-image);
  min-height: 358px;
  padding-top: var(--spacing-80);
  color: var(--main-light);
  display: flex;
  align-items: center;
  transition: ease all 0.3s;

  &.sticky-mobile {
    display: none;
  }

  .updated {
    font-size: 14px;
    display: flex;
    align-items: center;
    margin-top: 4px;

    svg {
      margin-right: 8px;
    }
  }

  .container {
    height: 100%;
    padding-top: var(--spacing-32);
    padding-bottom: var(--spacing-32);

    .row {
      height: 100%;

      .h3 {
        margin-bottom: var(--spacing-8);
        display: flex;
        align-items: center;
        flex-wrap: wrap;

        &:has(.label) {
          .course-name {
            margin-right: var(--spacing-8);
          }
        }

        .labels {
          display: inline-flex;
          align-items: center;
        }

        .label {
          vertical-align: middle;
          padding: 6px 8px;
          border-radius: 5px;
          width: max-content;
          font-size: 12px;
          font-weight: 600;
          line-height: 130%;
          text-transform: uppercase;
          color: rgba(0, 12, 31, 0.8);
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 4px;

          &:not(:last-child) {
            margin-right: 4px;
          }

          &.trending {
            background: var(--main-purple-light-2);
          }

          &.bestseller {
            background: var(--main-yellow-light-2);
          }

          &.top-rated {
            background: var(--accent-light-3);
          }

          &.new {
            background: var(--accent-shade-2);
            color: var(--main-light);
          }

          &.popular {
            background: var(--main-red-light-3);
          }

          &.free {
            background: var(--main-green-light-3);
            color: var(--main-dark);
          }
        }
      }

      .instructor {
        margin-top: 0;
        font-weight: 500;
        line-height: 120%;

        .course-rating {
          margin-top: var(--spacing-8);
        }

        a {
          color: var(--accent);
          font-weight: 600;
        }
      }

      .description {
        margin-top: var(--spacing-28);
        margin-bottom: 0;
        color: var(--main-light-shade-4);
        line-height: 150%;
      }

      ::v-deep .course-progress .wrapper {
        margin-right: var(--spacing-8);
      }

      ::v-deep .course-progress .progress-bar {
        width: 289px;
        border: 1px solid var(--main-dark-light-3);
        background: var(--main-dark-light-3);
      }

      .info {
        display: flex;
        align-items: center;
        font-size: 14px;
        font-weight: 500;
        margin-top: 16px;

        span:not(.dot) {
          display: flex;
          align-items: center;

          svg {
            margin-right: var(--spacing-8);
          }
        }

        span.dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--main-dark-light-4);
          display: inline-block;
          margin: 0 8px;
        }
      }
    }
  }

  &.hide {
    opacity: 0;
  }

  &.lms {
    padding-top: 0;

    .container .row .h3 {
      font-size: 32px;
    }
  }

  &.sticky {
    width: 100%;
    min-height: 93px;
    top: 80px;
    position: fixed;
    z-index: 3;
    transition: ease all var(--duration);
    padding: 0;
    opacity: 0;
    visibility: hidden;
    left: 0;
    right: 0;

    &.show {
      z-index: 4;
      opacity: 1;
      visibility: visible;
    }

    .container {
      padding: var(--spacing-32) 0;

      .row {
        .h3 {
          font-size: 24px;
          display: flex;
          align-items: center;

          .label {
            margin-left: var(--spacing-8);
          }
        }

        .instructor {
          font-size: 16px;
          margin-top: var(--spacing-8);

          a {
            color: var(--accent);
            font-weight: 600;
          }
        }
      }
    }

    &.lms {
      top: 64px;
      padding-left: 44px;
    }

    &.hide-sticky {
      opacity: 0;
    }

    &.sidebar-expanded {
      padding-left: var(--nav-bar-width);
    }
  }
}

@include bp($bp-laptop) {
  .hero-section {
    &.lms .container .row .h3,
    .container .row .h3 {
      font-size: 28px;
    }

    &.sticky {
      .container .row {
        .h3 {
          font-size: 24px;
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .hero-section {
    &.hide {
      opacity: 1;
    }

    &.sticky {
      display: none;
    }

    &.sticky-mobile {
      display: block;
      top: 68px;
      min-height: unset;
      border-bottom-right-radius: 0;
      background: linear-gradient(135deg, rgba(86, 191, 199, 0.2) 0.94%, rgba(86, 191, 199, 0) 100%), #000C1F;

      &.lms {
        top: 64px;
        padding-left: 0;

        .container {
          > div {
            .price-wrapper {
              .old-price {
                line-height: 23px;
              }

              .new-price {
                line-height: 45px;
              }
            }
          }
        }
      }

      &.banner {
        top: 102px;
      }

      &.show {
        z-index: 5;
      }

      .container {
        padding: 0;

        > div {
          padding: var(--spacing-8) var(--spacing-24);
          display: flex;
          justify-content: space-between;
          align-items: center;

          img {
            width: 92px;
            height: 52px;
            border-radius: 5px;
          }

          .price-wrapper {
            .old-price {
              text-decoration: line-through;
              font-size: 18px;
              font-weight: 500;
              margin-bottom: 0;
            }

            .new-price {
              font-size: 40px;
              font-weight: 500;
              margin-bottom: 0;
            }

            .caption {
              font-size: 14px;
              margin: 0;
              color: var(--main-light-shade-4);
              line-height: 150%;
            }
          }

          .limited-time-only-banner {
            position: absolute;
            top: -34px;
            left: 0;
            right: 0;
            padding: 12px 8px;
            color: var(--main-light);
            background: var(--main-red);
            font-size: 14px;
            font-weight: 600;
            line-height: 130%;
            text-transform: uppercase;
            text-align: center;
            height: 34px;
          }

          .btn {
            height: 52px;
            display: flex;
            align-items: center;
            justify-content: center;

            &.btn-primary-yellow {
              color: var(--main-dark);
            }
          }
        }
      }
    }
  }
}
</style>
